import { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Box,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import { FcGoogle } from "react-icons/fc";
import { FaGithub } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import api from "../../services/api";
import toast from "react-hot-toast";
import { jwtDecode } from "jwt-decode";
import { useMyContext } from "../../store/ContextApi";
import { motion } from "framer-motion";
import { clearAuthState, loginUser } from "../../State/Auth/Action";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

const apiUrl = process.env.REACT_APP_API_URL;

const Login = () => {
  const [loginValue, setLoginValue] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const { setToken } = useMyContext();
  const dispatch = useDispatch();

  const { userLoginResult, userLoginLoading, userLoginError } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    const token = localStorage.getItem("JWT_TOKEN");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const roles = decoded.roles ? (Array.isArray(decoded.roles) ? decoded.roles : decoded.roles.split(",")) : [];
        const isLearner = roles.some(role => role === "ROLE_LEARNER" || role === "LEARNER");
        
        if (isLearner) {
          navigate("/browse-courses");
        } else {
          navigate("/home");
        }
      } catch (error) {
        // If token decode fails, just go to home
        navigate("/home");
      }
    }
  }, [navigate]);

  const handleLogin = (e) => {
    e.preventDefault();

    if (!loginValue || !password) {
      toast.error("Please enter both email/username and password");
      return;
    }

    // try {
    const loginInput = {
      emailOrUsername: loginValue, // Update this field name based on your backend
      password,
    };

    dispatch(loginUser(loginInput));
  };

  useEffect(() => {
    const data = userLoginResult?.data;
    if (data?.jwtToken) {
      const token = data.jwtToken;
      const decoded = jwtDecode(token);
      localStorage.setItem("JWT_TOKEN", token);
      localStorage.setItem("USER", JSON.stringify({ username: decoded.sub }));
      setToken(token);
      toast.success("Login successful!");
      dispatch(clearAuthState());
      
      // Check if user is a learner and redirect accordingly
      const roles = decoded.roles ? (Array.isArray(decoded.roles) ? decoded.roles : decoded.roles.split(",")) : [];
      const isLearner = roles.some(role => role === "ROLE_LEARNER" || role === "LEARNER");
      
      if (isLearner) {
        navigate("/browse-courses");
      } else {
        navigate("/home");
      }
    } else if (userLoginError && userLoginError.success === false) {
      toast.error(userLoginError.message || "Login failed. Please try again.");
      dispatch(clearAuthState()); // 👈 Clear error after showing
    }
  }, [userLoginResult, userLoginError, navigate, setToken, dispatch]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-primary-200 to-secondary-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-float"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-gradient-to-r from-secondary-200 to-accent-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative w-full max-w-md"
      >
        <div className="glass p-8 rounded-3xl shadow-large border border-white/20">
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl mx-auto mb-4 flex items-center justify-center"
            >
              <span className="text-2xl">🎓</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2"
            >
              Welcome Back!
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-gray-600"
            >
              Sign in to continue your learning journey
            </motion.p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <TextField
                label="Email or Username"
                variant="outlined"
                type="text"
                value={loginValue}
                onChange={(e) => setLoginValue(e.target.value)}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#0ea5e9',
                  },
                }}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ color: '#0ea5e9', mr: 1 }} />,
                }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <TextField
                label="Password"
                variant="outlined"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleLogin(e);
                  }
                }}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#0ea5e9',
                  },
                }}
                InputProps={{
                  startAdornment: <LockIcon sx={{ color: '#0ea5e9', mr: 1 }} />,
                }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleLogin}
                sx={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #a855f7 100%)',
                  borderRadius: '12px',
                  py: 1.5,
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 8px 32px rgba(14, 165, 233, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0284c7 0%, #9333ea 100%)',
                    boxShadow: '0 12px 40px rgba(14, 165, 233, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Sign In
              </Button>
            </motion.div>

            {/* Links */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="flex justify-between text-sm"
            >
              <Link 
                to="/register" 
                className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                Create Account
              </Link>
              <Link
                to="/forgot-password"
                className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                Forgot Password?
              </Link>
            </motion.div>

            {/* Divider */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="flex items-center justify-between my-6"
            >
              <hr className="border-t border-gray-300 flex-1" />
              <span className="px-4 text-gray-500 font-medium">OR</span>
              <hr className="border-t border-gray-300 flex-1" />
            </motion.div>

            {/* Google Login */}
            <motion.a
              href={`${apiUrl}/oauth2/authorization/google`}
              className="flex items-center justify-center gap-3 w-full rounded-xl border-2 border-gray-200 bg-white/80 backdrop-blur-sm px-6 py-3 shadow-soft hover:shadow-medium hover:border-primary-300 transition-all duration-300 group"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FcGoogle className="text-2xl group-hover:scale-110 transition-transform" />
              <span className="font-medium text-gray-700 group-hover:text-gray-900">
                Continue with Google
              </span>
            </motion.a>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
